import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { LegacyRowActionsConfigProp } from '@ctw/shared/components/tables/legacy-table';
import {
  type FetchableResource,
  type FetchableResourceIdentifier,
  fetchResourceByTypeAndIds,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import { resourceModalActionForModel } from '@ctw/shared/context/_internal/resource/resource-modal';
import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import {
  type ModalLayout,
  type OpenModalAction,
  type UseModalOptions,
  useModal,
} from '@ctw/shared/context/modal-context';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  type ResourceTypeModelMap,
  isFetchableResourceType,
  isValidResourceId,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { pathWithParameters } from '@ctw/shared/utils/url';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { GraphQLClient } from 'graphql-request';
import { isEmpty, uniqueId } from 'lodash-es';
import {
  Fragment,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { SetOptional } from 'type-fest';

export interface ResourceModalState<
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> {
  isModalOpen: boolean;
  openResourceModal: (options: UseResourceModalOpenModalArgs<TResourceType>) => void;
  reloadResource: () => Promise<FetchableModelType<TResourceType> | null>;
  urlDeepLinkResource: FetchableResource | null;
  fetchedDeepLinkResource: TModel | null;
  setUrlDeepLinkResource: (specifier: FetchableResourceIdentifier) => void;
  setModal: (Modal: ReturnType<typeof useModal>) => void;
}

const ResourceModalContext = createContext<ResourceModalState | null>(null);

const defaultResourceModalLayout: ModalLayout = {
  mode: 'full',
  overflowBehavior: 'scroll',
  fullbleed: true,
  showCloseButton: false,
} as const;

interface ResourceModalProviderProps extends PropsWithChildren {
  enableDeepLinking?: boolean;
  defaultLayout?: ModalLayout;
}

export const ResourceModalProvider = <
  TResourceType extends FetchableResourceType = FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
>({
  enableDeepLinking = true,
  defaultLayout = defaultResourceModalLayout,
  children,
}: ResourceModalProviderProps) => {
  const providerTelemetry = useTelemetry();
  const { navigate } = useZuiContext();
  const { requestContext, graphqlClient, ctwFetch } = useCTW();
  const queryClient = useQueryClient();
  const [urlDeepLinkResource, internalSetUrlDeepLinkResource] = useState<FetchableResource | null>(
    null,
  );
  const [modal, setModal] = useState<ReturnType<typeof useModal> | null>(null);
  const patientContext = usePatientContextIfAvailable();
  const [fetchedDeepLinkResource, setFetchedDeepLinkResource] = useState<TModel | null>(null);
  const canFetchDeepLinkedResource = useMemo(
    () => isEmpty(fetchedDeepLinkResource) && !isEmpty(urlDeepLinkResource) && patientContext,
    [patientContext, fetchedDeepLinkResource, urlDeepLinkResource],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only once to set default on first render
  useEffect(() => {
    if (modal) {
      modal.setLayout(defaultLayout);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const removeUrlDeepLinkResource = useCallback(() => {
    const url = new URL(window.location.href);
    const urlResourceType = url.searchParams.get(
      'modalResourceType',
    ) as FetchableResourceType | null;
    const urlResourceId = url.searchParams.get('modalResourceId');

    if (urlResourceType || urlResourceId) {
      navigate(
        pathWithParameters(
          window.location.pathname,
          {
            modalResourceType: null,
            modalResourceId: null,
          },
          ['builderId', 'resourceId', 'resourceType'],
        ),
      );
    }
  }, []);

  const openResourceModal = useCallback(
    ({
      model,
      patient: explicitPatient,
      resourceId: explicitResourceId,
      resourceType: explicitResourceType,
      ...openModalArgs
    }: UseResourceModalOpenModalArgs<TResourceType>) => {
      const resourceId = (model ? model.id : explicitResourceId) ?? '';
      const resourceType = (model ? model.resourceType : explicitResourceType) ?? '';
      const patient = explicitPatient ?? patientContext?.patient;

      const url = new URL(globalThis.location.href);
      const urlResourceId = url.searchParams.get('resourceId');

      if (enableDeepLinking && urlResourceId !== resourceId) {
        setUrlDeepLinkResource({ resourceType, resourceId });
      }

      const effectiveTelemetry = openModalArgs.telemetry ?? providerTelemetry;
      effectiveTelemetry.setScopedContextProperties({
        ...openModalArgs.telemetryContext,
        resourceId,
        resourceType,
      });

      const performCanonicalAction = (model: TModel, patient: PatientModel) => {
        const canonicalAction =
          openModalArgs.action?.({
            patient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeModal: () => modal?.closeModal(),
            model: model as never,
            reloadResource: reloadResource as never,
          }) ??
          resourceModalActionForModel(model)?.({
            patient,
            ctwFetch,
            graphqlClient,
            requestContext,
            telemetry: effectiveTelemetry,
            queryClient,
            closeModal: () => modal?.closeModal(),
            model: model as never,
            reloadResource: reloadResource as never,
          });

        if (modal && canonicalAction) {
          modal.openModal({
            ...openModalArgs,
            action: {
              ...canonicalAction,
              content: {
                body: <Fragment key={uniqueId()}>{canonicalAction.content.body}</Fragment>,
              },
            },
          });
        }
      };

      if (model && patient) {
        performCanonicalAction(model as never, patient);
      } else if (patient) {
        void fetchResourceByTypeAndIds({
          resourceType: resourceType as never,
          resourceIds: [resourceId],
          ctwFetch,
          graphqlClient,
          requestContext,
          queryClient,
          patient,
          telemetry: effectiveTelemetry,
        }).then((fetchedResources) => {
          performCanonicalAction(fetchedResources[0], patient);
        });
      } else {
        effectiveTelemetry.trackError({
          error: new Error('Cannot open resource modal: patient context required'),
          context: { resourceId, resourceType },
        });
        throw new Error(
          `Resource modal requires patient context. ResourceType: ${resourceType}, ResourceId: ${resourceId}`,
        );
      }
    },
    [
      modal,

      ctwFetch,
      graphqlClient,
      requestContext,
      queryClient,
      providerTelemetry,
      patientContext,
      enableDeepLinking,
    ],
  );

  const fetchResource = useCallback(async (): Promise<TModel | null> => {
    if (!(canFetchDeepLinkedResource && urlDeepLinkResource?.resourceId)) {
      return null;
    }

    const reloadedResource = patientContext?.patient
      ? (
          await fetchResourceByTypeAndIds({
            telemetry: providerTelemetry,
            ctwFetch,
            graphqlClient,
            requestContext,
            queryClient,
            patient: patientContext?.patient,
            resourceType: urlDeepLinkResource.resourceType,
            resourceIds: [urlDeepLinkResource.resourceId],
          })
        )[0]
      : null;

    return reloadedResource as never;
  }, [
    canFetchDeepLinkedResource,
    providerTelemetry,
    patientContext,
    ctwFetch,
    graphqlClient,
    requestContext,
    queryClient,
    urlDeepLinkResource?.resourceId,
    urlDeepLinkResource?.resourceType,
  ]);

  const reloadResource = useCallback(async () => {
    const fetchedResource = await fetchResource();

    setFetchedDeepLinkResource(fetchedResource);

    if (fetchedResource) {
      openResourceModal({
        telemetry: providerTelemetry,
        model: fetchedResource as never,
        reloadResource,
      });
    }

    return fetchedResource;
  }, [fetchResource, providerTelemetry, openResourceModal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-run when `navigate` changes
  const setUrlDeepLinkResource = useCallback(
    ({ resourceType, resourceId }: FetchableResourceIdentifier) => {
      if (
        isFetchableResourceType(resourceType) &&
        isValidResourceId(resourceId) &&
        resourceId !== urlDeepLinkResource?.resourceId
      ) {
        navigate(
          pathWithParameters(
            window.location.pathname,
            {
              modalResourceType: resourceType,
              modalResourceId: resourceId,
            },
            ['builderId', 'resourceId', 'resourceType'],
          ),
        );
        internalSetUrlDeepLinkResource({ resourceType, resourceId });
      }
    },
    [reloadResource, urlDeepLinkResource],
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    const urlResourceType = url.searchParams.get(
      'modalResourceType',
    ) as FetchableResourceType | null;
    const urlResourceId = url.searchParams.get('modalResourceId');

    if (enableDeepLinking && urlResourceType !== null && urlResourceId !== null) {
      internalSetUrlDeepLinkResource({ resourceType: urlResourceType, resourceId: urlResourceId });
    }
  }, [enableDeepLinking]);

  useEffect(() => {
    if (
      urlDeepLinkResource?.resourceId &&
      (!fetchedDeepLinkResource?.id ||
        fetchedDeepLinkResource.id !== urlDeepLinkResource.resourceId)
    ) {
      void fetchResource().then((fetchedResource) => {
        if (fetchedResource) {
          setFetchedDeepLinkResource(fetchedResource);
          openResourceModal({
            telemetry: providerTelemetry,
            model: fetchedResource,
            reloadResource,
          });
        }
      });
    }
  }, [
    urlDeepLinkResource,
    fetchResource,
    fetchedDeepLinkResource,
    openResourceModal,
    reloadResource,
    providerTelemetry,
  ]);

  return (
    <ResourceModalContext.Provider
      value={{
        isModalOpen: modal?.isModalOpen ?? false,
        openResourceModal: openResourceModal as never,
        reloadResource,
        urlDeepLinkResource,
        setUrlDeepLinkResource,
        fetchedDeepLinkResource,
        setModal: (newModal) => {
          if (!modal) {
            newModal.onModalEvent('ResourceModal', (eventType) => {
              if (eventType === 'close') {
                requestAnimationFrame(() => removeUrlDeepLinkResource());
              }
            });
            setModal(newModal);
            void reloadResource();
          }
        },
      }}
    >
      {children}
    </ResourceModalContext.Provider>
  );
};

export interface OpenResourceModalAction extends OpenModalAction {}

interface UseResourceModalCommonOpenModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends SetOptional<OpenModalAction, 'content'> {
  telemetry: ReturnType<typeof useTelemetry>;
  telemetryContext?: TelemetryContext;
  reloadResource?: () => Promise<TModel | null>;
  patient?: PatientModel;
  action?: (args: UseResourceModalActionArgs<TResourceType, TModel>) => OpenResourceModalAction;
}

interface UseResourceModalOpenWithModelModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceModalCommonOpenModalArgs<TResourceType, TModel> {
  model: TModel;
  resourceId?: never;
  resourceType?: never;
}

interface UseResourceModalOpenWithResourceTypeAndIdModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends UseResourceModalCommonOpenModalArgs<TResourceType, TModel> {
  resourceId: string;
  resourceType: string;
  model?: never;
}

export type UseResourceModalOpenModalArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> =
  | UseResourceModalOpenWithModelModalArgs<TResourceType, TModel>
  | UseResourceModalOpenWithResourceTypeAndIdModalArgs<TResourceType, TModel>;

export interface UseResourceModalActionArgs<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType>,
> {
  ctwFetch: CTWState['ctwFetch'];
  graphqlClient: GraphQLClient;
  requestContext: CTWRequestContext;
  telemetry: ReturnType<typeof useTelemetry>;
  queryClient: QueryClient;
  model: TModel;
  patient: PatientModel;
  closeModal: () => void;
  reloadResource: () => Promise<TModel | null>;
}

export interface UseResourceModalOptions<
  TResourceType extends FetchableResourceType,
  TModel extends FetchableModelType<TResourceType> = FetchableModelType<TResourceType>,
> extends Omit<UseModalOptions, 'boundaryName'> {
  resourceActions?: TResourceType extends keyof ResourceTypeModelMap
    ? (model: TModel) => LegacyRowActionsConfigProp<TModel>
    : never;
  enableViewFhirButton?: boolean;
  enableDismissAndReadActions?: boolean;
  action?: (args: UseResourceModalActionArgs<TResourceType, TModel>) => OpenResourceModalAction;
}

const defaultUseResourceModalLayout: ModalLayout = {
  mode: 'full',
  overflowBehavior: 'hidden',
  fullbleed: true,
  showCloseButton: false,
} as const;

export const useResourceModal = <TResourceType extends FetchableResourceType>({
  layout = defaultUseResourceModalLayout,
  telemetryContext: hookExplicitTelemetryContext,
  onModalEvent,
  resourceActions,
  enableViewFhirButton = true,
  enableDismissAndReadActions = false,
  action,
}: UseResourceModalOptions<TResourceType> = {}) => {
  const context = useContext(ResourceModalContext);

  if (!context) {
    throw new Error('useResourceModal must be used inside a ResourceModalContext');
  }

  const hookLocalTelemetry = useTelemetry();

  const modal = useModal({
    boundaryName: 'ResourceModal',
    layout,
    telemetryContext: {
      ...hookLocalTelemetry.getScopedContextProperties(),
      ...hookExplicitTelemetryContext,
      withinResourceModal: true,
    },
    onModalEvent,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change these options after mount
  useEffect(() => {
    context.setModal(modal);
  }, [modal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Can't change these options after mount
  const openResourceModal = useCallback(
    (options: UseResourceModalOpenModalArgs<TResourceType>) => {
      const resourceId = options?.model?.id ?? options.resourceId;
      const resourceType = options?.model?.resourceType ?? options.resourceType;

      requestAnimationFrame(() => {
        context.openResourceModal({
          action,
          resourceActions,
          enableViewFhirButton,
          enableDismissAndReadActions,
          ...options,
          telemetryContext: {
            ...hookLocalTelemetry.getScopedContextProperties(),
            ...hookExplicitTelemetryContext,
            ...options.telemetryContext,
            resourceId,
            resourceType,
            withinResourceModal: true,
          },
        } as never);
      });
    },
    [modal, action, context],
  );

  return {
    setUrlDeepLinkResource: context.setUrlDeepLinkResource,
    openResourceModal,
  };
};
